import { ProductType } from 'constants/enums/productTypes'
import { PropertyType } from 'constants/enums/propertyTypes'
import { PurposeOfLoan } from 'constants/enums/purposeOfLoanTypes'
import { TranslationFile } from 'i18n'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { useTranslation } from 'react-i18next'
import { checkIsMobile, FormatEnumToList } from 'utils/commonhelpers'

export const ApplicationBankInfoForm = (data: IApplicationInformation) => {
	const { t } = useTranslation()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoForm',
		collapsable: false,
		show: true,
		visible: true,
		market: 'es',
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'ibanVal',
				type: 'text',
				label: t('forms:Iban'),
				description: t('forms:IbanDescription'),
				value: data.bankInfo.iban,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '\\b^(ES)\\s*(\\d{2})\\s*(\\d{4})\\s*(\\d{4})\\s*(\\d{2})\\s*(\\d{10})\\b$',
						message: t('InvalidFormat'),
					},
				],
				visible: true,
				col: 12,
			},
		],
	}
}

export const ApplicationBankInfoFormEsCreditCard = (
	data: IApplicationInformation,
) => {
	const { t } = useTranslation()
	const formatIbans = (ibans: string[]) => {
		let ibanOptions: any[] = []
		ibans.map((key: string) =>
			ibanOptions.push({ value: key, label: t(key.toString()) }),
		)
		return ibanOptions
	}

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoForm',
		collapsable: false,
		show: true,
		visible: true,
		market: 'es',
		fullSize: true,
		col: 12,
		controls: [
			{
				options: formatIbans(
					JSON.parse(sessionStorage.getItem('psd2Ibans')!),
				),
				required: true,
				controlName: 'ibanVal',
				type: 'dropdown',
				label: t('forms:Iban'),
				description: t('forms:IbanDescriptionDirectDebit'),
				value: data.bankInfo.iban,
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'directDebitConsent',
				type: 'checkbox',
				label: t('forms:DirectDebitConsent'),
				value: false,
				visible: true,
				col: 12,
			},
		],
	}
}

export const ApplicationBankInfoFormFi = (data: IApplicationInformation) => {
	const { t } = useTranslation()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoFormFi',
		collapsable: false,
		show: true,
		visible: true,
		market: 'fi',
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'ibanVal',
				type: 'text',
				label: t('forms:Iban'),
				value: data.bankInfo.iban,
				maxLength: 18,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(f|F)(i|I)([0-9]{16})$',
						message: t('InvalidIban'),
					},
				],
				visible: true,
				col: 12,
			},
		],
	}
}

export const ApplicationBankInfoFormDk = (data: IApplicationInformation) => {
	const { t } = useTranslation([TranslationFile.Forms])
	const isMobile = checkIsMobile()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoFormDk',
		collapsable: false,
		show: true,
		visible: true,
		market: 'dk',
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'directDebitClearingNumber',
				type: 'text',
				label: t('forms:DirectDebitClearingNumber'),
				value: data.bankInfo?.directDebitClearingNumber,
				visible: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(\\d{4})?$',
						message: t(
							'forms:ValidationMsgDirectDebitClearingNumber',
						),
					},
				],
				col: isMobile ? 12 : 4,
			},
			{
				required: true,
				controlName: 'directDebitAccount',
				type: 'text',
				label: t('forms:DirectDebitAccountNumber'),
				value: data.bankInfo?.directDebitAccount,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(\\d{7,10})?$',
						message: t('forms:ValidationMsgDirectDebitAccount'),
					},
				],
				visible: true,
				col: isMobile ? 12 : 8,
			},
		],
	}
}

export const ApplicationBankInfoFormLt = (data: IApplicationInformation) => {
	const { t } = useTranslation()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoFormLt',
		collapsable: false,
		show: true,
		visible: true,
		market: 'lt',
		fullSize: true,
		description: t('forms:BankInfoFormDescription'),
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'ibanVal',
				type: 'text',
				label: t('forms:Iban'),
				value: data.bankInfo.iban,
				maxLength: 20,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(l|L)(t|T)([0-9]{18})$',
						message: t('InvalidIban'),
					},
				],
				visible: true,
				col: 12,
			},
			{
				options: [
					{
						value: false,
						label: t('No'),
					},
					{
						value: true,
						label: t('Yes'),
					},
				],
				required: true,
				controlName: 'beneficialOwner',
				type: 'dropdown',
				label: t('forms:BeneficialOwner'),
				tooltip: t('forms:BeneficialOwnerInfoDescription'),
				value: true,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownBoolean'),
			},
			{
				options: [
					{
						value: false,
						label: t('No'),
					},
					{
						value: true,
						label: t('Yes'),
					},
				],
				required: true,
				controlName: 'pep',
				type: 'dropdown',
				label: t('forms:PoliticallyExposedPerson'),
				tooltip: t('forms:PEPInfoDescription'),
				value: false,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownBoolean'),
			},
			{
				options: FormatEnumToList(
					PurposeOfLoan,
					PropertyType.PurposeOfLoan,
					TranslationFile.Dropdown,
				),
				required: data?.product === ProductType.LtuCashLoan,
				controlName: 'purposeOfLoan',
				type: 'dropdown',
				label: t('forms:ApplicationPurposeOfLoan'),
				value:
					data?.product === ProductType.LtuCashLoan
						? 'Consumption'
						: null,
				visible: data?.product === ProductType.LtuCashLoan,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownPurpose'),
			},
		],
	}
}

export const ApplicationBankInfoFormDe = (data: IApplicationInformation) => {
	const { t } = useTranslation()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoFormDe',
		collapsable: false,
		show: true,
		visible: true,
		market: 'de',
		fullSize: true,
		description: t('forms:BankInfoFormDescription'),
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'ibanVal',
				type: 'text',
				label: t('forms:Iban'),
				description: t('forms:IbanDescription'),
				descriptionAbove: true,
				value: data.bankInfo.iban,
				maxLength: 22,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(d|D)(e|E)([0-9]{20})$',
						message: t('InvalidIban'),
					},
				],
				visible: true,
				col: 12,
			},
			{
				options: [
					{
						value: true,
						label: t('dropdown:Yes'),
					},
					{
						value: false,
						label: t('dropdown:No'),
					},
				],
				required: true,
				controlName: 'beneficialOwner',
				type: 'dropdown',
				label: t('forms:BeneficialOwner'),
				// tooltip: t('forms:BeneficialOwnerInfoDescription'),
				value: true,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownBoolean'),
			},
			{
				options: FormatEnumToList(
					PurposeOfLoan,
					PropertyType.PurposeOfLoan,
					TranslationFile.Dropdown,
				),
				required: true,
				controlName: 'purposeOfLoan',
				type: 'dropdown',
				label: t('forms:ApplicationPurposeOfLoan'),
				// value: data.,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownPurpose'),
			},
		],
	}
}
export const ApplicationBankInfoFormIt = (data: IApplicationInformation) => {
	const { t } = useTranslation()

	return {
		fieldSetName: '',
		formName: 'ApplicationBankInfoFormIt',
		collapsable: false,
		show: true,
		visible: true,
		market: 'it',
		fullSize: true,
		description: t('forms:BankInfoFormDescription'),
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'ibanVal',
				type: 'text',
				label: t('forms:Iban'),
				value: data.bankInfo.iban,
				maxLength: 27,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(i|I)(t|T)([0-9]{2}[A-Za-z][0-9]{22})$',
						message: t('InvalidIban'),
					},
				],
				visible: true,
				col: 12,
			},
		],
	}
}
